import { z } from "zod";

// =============================================================================
// Tenant-Wide QVD Analysis
// Orphan QVDs (no consuming app) + QVD columns not loaded by any app.
// =============================================================================

// --- Scope + start-run input / job payload ---
export const qvdAnalysisScopeSchema = z.object({
  spaceIds: z.array(z.string()).default([]),
  qvdIds: z.array(z.string()).default([]),
  // Human-readable space names captured when the run is started, so the UI can
  // show them without a live Qlik lookup. `spaceIds` stays the load-bearing
  // filter; `spaces` is display-only and empty on runs created before this.
  spaces: z.array(z.object({ spaceId: z.string(), spaceName: z.string() })).default([]),
  // Glob patterns, matched case-insensitively by the worker against the file's
  // relative PATH when the pattern contains "/", against its BASE NAME otherwise.
  // ANY-match inclusion list: a file is in scope when it matches at least one, and
  // the filter narrows the WHOLE analysis (footprint included), like spaceIds does.
  // Empty = no name filter (and empty on runs created before this existed — jsonb,
  // no migration).
  namePatterns: z.array(z.string()).default([]),
});
export type QvdAnalysisScope = z.infer<typeof qvdAnalysisScopeSchema>;

export const startQvdAnalysisSchema = z
  .object({
    connectionId: z.string().uuid(),
    scope: qvdAnalysisScopeSchema.optional(),
    forceFullRefresh: z.boolean().default(false),
    // What the run evaluates — the drawer's "what to evaluate" section (all on
    // by default). Total space is a cheap inventory-only pass; orphans and
    // unused columns are the lineage phases.
    evaluateTotalSpace: z.boolean().default(true),
    evaluateOrphans: z.boolean().default(true),
    evaluateUnusedColumns: z.boolean().default(true),
  })
  .superRefine((v, ctx) => {
    if (!v.evaluateTotalSpace && !v.evaluateOrphans && !v.evaluateUnusedColumns) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Select at least one thing to evaluate.",
        path: ["evaluateTotalSpace"],
      });
    }
    // The unused-columns phase reuses the orphan phase's consumed-QVD list, so
    // it is a real dependency (not only UX): columns requires orphans.
    if (v.evaluateUnusedColumns && !v.evaluateOrphans) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Unused columns requires orphan detection.",
        path: ["evaluateUnusedColumns"],
      });
    }
  });
export type StartQvdAnalysisInput = z.infer<typeof startQvdAnalysisSchema>;

// Not-analyzable reasons that are safe to reprocess: transient failures that
// survived retries — real HTTP 429 throttling (throttled_retriable), other
// transient 5xx/timeout/network errors (transient_error), auth failures
// (lineage_401 — recoverable by fixing the connection's credentials, and
// previously bucketed under transient_error), and consumers_unverified — a
// LEGACY reason: the orphan phase no longer withholds a verdict when an app
// answers neither its reload log nor its lineage (it emits the orphans and
// discloses the mute apps instead, see appsUnverified* below), but runs that
// predate that change still carry these rows and must stay reprocessable.
//
// The column-phase reasons below are retriable because the evidence they lack
// can appear without any user action:
//   - no_field_source: neither the reload log nor field lineage produced a
//     usable field source. A later run can read the log (or the lineage) fine,
//     and reprocessing is the only way an existing run picks up the lineage
//     fallback at all.
//   - field_inventory_mismatch: the lineage inventory was non-empty but could
//     not be matched against the QVD header (ambiguous label or empty
//     intersection). The next reload re-materializes the lineage and usually
//     realigns it with the header.
//   - field_lineage_unavailable: Qlik has no lineage node for at least one of
//     the QVD's fields (LNG-1/LNG-3). It typically materializes after the
//     consuming app reloads.
//   - schema_unreadable: the catalog dataset that carries a Parquet file's
//     column list could not be listed or read (403/404/transient). The catalog
//     entry appears on its own once the file is re-indexed, so a later run can
//     answer where this one could not.
//   - schema_stale: the catalog dataset that carries a Parquet file's column
//     list describes a DIFFERENT revision of the file than the inventory saw
//     (its operational size / update time do not match), or carries no
//     comparable revision marker at all. The denominator would not describe
//     the file the apps actually read, so no verdict is published; Qlik
//     re-indexes on its own and a later run answers it.
// Permanent reasons (lineage_403, not_found, lineage_shape_unknown,
// space_list_failed, no_columns_loaded, empty_field_inventory) are excluded
// from reprocessing.
export const RETRIABLE_QVD_REASONS = [
  "throttled_retriable",
  "transient_error",
  "lineage_401",
  "consumers_unverified",
  "no_field_source",
  "field_inventory_mismatch",
  "field_lineage_unavailable",
  "schema_unreadable",
  "schema_stale",
] as const;
export type RetriableQvdReason = (typeof RETRIABLE_QVD_REASONS)[number];

// Job payload (worker) — carries runId so the worker persists to the right run.
export const analyzeQvdsPayloadSchema = z.object({
  runId: z.string().uuid(),
  connectionId: z.string().uuid(),
  scope: qvdAnalysisScopeSchema,
  forceFullRefresh: z.boolean().default(false),
  // Scope of what to evaluate (mirror of the start input; default true so a
  // legacy job payload behaves as "everything evaluated").
  evaluateTotalSpace: z.boolean().default(true),
  evaluateOrphans: z.boolean().default(true),
  evaluateUnusedColumns: z.boolean().default(true),
  computeColumnSizes: z.boolean().default(false),
  // When true the run is a re-open of an existing run: only the retriable
  // not-analyzable subset is analyzed and the delta is merged back into the
  // same run's coverage (see the worker's reprocess path).
  reprocess: z.boolean().default(false),
  // When true the run is a re-open of an existing run to CONTINUE the QVDs it
  // never reached ("not_processed", e.g. a run that hit the safety timeout). The
  // worker derives the subset itself (live inventory minus the already-resulted
  // QVDs; the connection-wide column cache is not part of that subtraction) and
  // merges the delta back into the run's baseline coverage. Distinct from
  // `reprocess` (which targets the retriable not-analyzable subset carried in
  // scope.qvdIds).
  resume: z.boolean().default(false),
});
export type AnalyzeQvdsPayload = z.infer<typeof analyzeQvdsPayloadSchema>;

// --- Scope options (run drawer selector; fetched LIVE from Qlik) ---
export const qvdScopeSpaceSchema = z.object({
  spaceId: z.string(),
  spaceName: z.string(),
});
export type QvdScopeSpace = z.infer<typeof qvdScopeSpaceSchema>;

export const qvdScopeQvdSchema = z.object({
  qvdId: z.string(), // identity used in scope.qvdIds
  qvdName: z.string(),
  spaceName: z.string().nullable(),
});
export type QvdScopeQvd = z.infer<typeof qvdScopeQvdSchema>;

export const qvdScopeOptionsResponseSchema = z.object({
  data: z.object({
    spaces: z.array(qvdScopeSpaceSchema),
    qvds: z.array(qvdScopeQvdSchema),
    source: z.literal("live"), // scope options are fetched live from Qlik
    fetchedAt: z.string().nullable(),
  }),
});
export type QvdScopeOptions = z.infer<typeof qvdScopeOptionsResponseSchema>["data"];

// --- Progress + coverage ---
export const qvdAnalysisProgressSchema = z.object({
  total: z.number(),
  processed: z.number(),
  failed: z.number(),
  currentItem: z.string().optional(),
  // Files discovered so far. Set only during the inventory phase (where
  // total/processed count spaces scanned, not QVDs), so the UI can show the
  // running file tally while the tenant is being listed.
  found: z.number().optional(),
});
export type QvdAnalysisProgress = z.infer<typeof qvdAnalysisProgressSchema>;

// A QVD whose lineage couldn't be read (surfaced so the user can see + re-run it).
export const notAnalyzableQvdSchema = z.object({
  qvdIdentity: z.string(),
  qvdName: z.string(),
  qvdPath: z.string(),
  spaceName: z.string().nullable(),
  spaceId: z.string().nullable().default(null),
  phase: z.enum(["orphans", "columns"]),
  reason: z.string(),
});
export type NotAnalyzableQvd = z.infer<typeof notAnalyzableQvdSchema>;

// --- Total-space footprint (inventory-only; covers all file types, not just QVDs) ---

// Per-file-type breakdown entry. `ext` is the lowercased suffix (no dot); ""
// means the file had no extension. An "outros" (others) aggregate bucket may be
// present when the tail beyond the top types is folded together.
export const qvdFootprintTypeSchema = z.object({
  ext: z.string(),
  bytes: z.number(),
  fileCount: z.number(),
});
export type QvdFootprintType = z.infer<typeof qvdFootprintTypeSchema>;

// Per-space footprint entry. Powers the "size by space" section; the first
// entry (spaces are sorted by bytes desc) also feeds the "largest space" KPI.
export const qvdSpaceUsageSchema = z.object({
  spaceName: z.string(),
  bytes: z.number(),
  fileCount: z.number(),
});
export type QvdSpaceUsage = z.infer<typeof qvdSpaceUsageSchema>;

// One app the run could not verify: named so the reader can find it, plus the
// cause in the same vocabulary the per-app column view speaks ("forbidden" |
// "no_successful_reload" | "unreadable") — the only distinctions that imply a
// different action. Left as a plain string (not an enum) so a new worker bucket
// never fails validation of a whole run's coverage.
export const unverifiedAppSchema = z.object({
  appId: z.string(),
  appName: z.string().default(""),
  spaceName: z.string().default(""),
  // The space ID, shown when the NAME is empty — which is the norm here: the
  // permission that blocked the app's reload log also hides its space from the
  // space listing, so the name is missing exactly on the apps that matter.
  // Defaults to "" for runs persisted before it existed (jsonb, no migration).
  spaceId: z.string().default(""),
  // When the app last reloaded (RFC3339). The cause "no_successful_reload"
  // means no reload log is available NOW — Qlik expires them — not that the app
  // never ran, and this date is what says so on screen.
  // Defaults to "" for runs persisted before it existed (jsonb, no migration).
  lastReloadTime: z.string().default(""),
  cause: z.string(),
});
export type UnverifiedApp = z.infer<typeof unverifiedAppSchema>;

export const qvdAnalysisCoverageSchema = z.object({
  qvdsInventoried: z.number(),
  qvdsAnalyzed: z.number(),
  qvdsNotAnalyzable: z.number(),
  columnsNotAnalyzable: z.number(),
  orphanCount: z.number(),
  unloadedColumnQvdCount: z.number(),
  qvdsFromCache: z.number(),
  reclaimableBytes: z.number(),
  spacesInventoried: z.number(),
  spacesNotInventoried: z.number(),
  // reason -> count (e.g. "lineage_403", "absent_from_graph", "not_found",
  //   "throttled_retriable", "space_list_failed")
  reasons: z.record(z.string(), z.number()).default({}),
  // The QVDs that couldn't be read (capped; qvdsNotAnalyzable holds the true total).
  notAnalyzable: z.array(notAnalyzableQvdSchema).default([]),
  // Σ disk bytes of not-loaded columns across sized QVDs (potential savings).
  // Mirror of reclaimableBytes; 0 when the size phase did not run.
  unloadedColumnBytes: z.number().default(0),
  // --- Size-phase failures (opt-in). sizeFailedCount is how many QVDs with
  // unloaded columns could not be sized; sizeAccessDeniedSpaces are the spaces
  // whose access pre-flight failed (the connection identity can't read their
  // DataFiles connection). Surfaced so a silent size failure is alerted. ---
  sizeFailedCount: z.number().default(0),
  sizeAccessDeniedSpaces: z.array(z.string()).default([]),
  // How many analyzed QVDs still have unloaded columns awaiting sizing (rows
  // whose sizeStatus is neither "sized" nor "size_failed"). Derived from the
  // persisted rows at finalize (only when the run requested sizing), so it is
  // always consistent with them and converges to 0 as a resume drains them. > 0
  // means the size phase was interrupted and the run stays resumable.
  sizePendingCount: z.number().default(0),
  // How many analyzed rows can never have per-column sizes (Parquet: Qlik Cloud
  // exposes no file content). Derived from the persisted rows at finalize —
  // unlike sizePendingCount this is NOT gated on the run requesting sizing, as
  // the format is unmeasurable either way. These rows are excluded from
  // unloadedColumnBytes, so the UI discloses the count wherever bytes are shown.
  sizeUnavailableCount: z.number().default(0),
  // --- Total-space footprint (present only when evaluateTotalSpace ran). The
  // UI gates on footprintFileCount > 0, so old runs (no footprint) render
  // cleanly instead of showing "0 B". ---
  footprintTotalBytes: z.number().default(0),
  footprintFileCount: z.number().default(0),
  footprintByType: z.array(qvdFootprintTypeSchema).default([]),
  // Per-space footprint, sorted by bytes desc (spaces are naturally bounded, so
  // this is not capped). footprintBySpace[0] is the "largest space".
  footprintBySpace: z.array(qvdSpaceUsageSchema).default([]),

  // --- Three-stage orphan pipeline stats (family sampling + app-centric union
  // + audited remainder). auditSampled/auditContradicted are the stage-3
  // audit's measured false-orphan signal for THIS run. Defaults keep runs from
  // before the pipeline parsing. ---
  familiesDetected: z.number().default(0),
  qvdsInFamilies: z.number().default(0),
  qvdsSampled: z.number().default(0),
  qvdsResolvedByApps: z.number().default(0),
  appsQueried: z.number().default(0),
  appsUnreadable: z.number().default(0),
  auditSampled: z.number().default(0),
  auditContradicted: z.number().default(0),
  appsScanned: z.number().int().optional().default(0),
  appsFromCache: z.number().int().optional().default(0),
  appsLogUnreadable: z.number().int().optional().default(0),
  qvdsResolvedByLogs: z.number().int().optional().default(0),
  qvdsWrittenNeverRead: z.number().int().optional().default(0),
  // How many QVDs got NO column verdict because one of their consuming apps has
  // no field evidence and Qlik's lineage could not answer for it either (see
  // unloadedColumnsQvdSchema.evidenceIncomplete). Drives
  // the "incomplete evidence" tab's count; disjoint from unloadedColumnQvdCount,
  // which only counts QVDs that DO have not-loaded columns.
  evidenceIncompleteCount: z.number().int().optional().default(0),
  // The subset of orphanCount/reclaimableBytes made of files whose only
  // consumption evidence came from the name family (orphanQvdSchema
  // .consumedByFamilyOnly): possibly in use. The totals include them (they are on
  // the orphan list); the UI subtracts these so the headline "reclaimable" never
  // counts bytes the row itself calls uncertain.
  familyOnlyCount: z.number().int().optional().default(0),
  familyOnlyBytes: z.number().optional().default(0),
  // --- Disclosure: apps this run learned NOTHING about (their reload log was
  // inaccessible AND their lineage could not be read either). The orphan list is
  // emitted anyway — withholding it dropped a real tenant to 6% coverage — so
  // the gap is shown instead: one of these apps could be the sole consumer of a
  // file on the list. Nothing is blocked by it.
  //
  // appsUnverifiedByCause is cause -> count and is EXACT; appsUnverified is
  // capped (the jsonb must stay bounded) and appsUnverifiedOmitted says how many
  // the list left out, so a truncated list is never silent.
  appsUnverifiedByCause: z.record(z.string(), z.number()).optional().default({}),
  appsUnverified: z.array(unverifiedAppSchema).optional().default([]),
  appsUnverifiedOmitted: z.number().int().optional().default(0),
});
export type QvdAnalysisCoverage = z.infer<typeof qvdAnalysisCoverageSchema>;

// --- Run status DTO ---
export const qvdAnalysisRunStatusSchema = z.enum([
  "pending",
  "processing",
  "completed",
  "failed",
  "cancelling",
  "cancelled",
]);
export type QvdAnalysisRunStatus = z.infer<typeof qvdAnalysisRunStatusSchema>;

export const qvdAnalysisPhaseSchema = z.enum([
  "inventory",
  "consumption",
  "orphans",
  "columns",
  "sizes",
  "done",
]);
export type QvdAnalysisPhase = z.infer<typeof qvdAnalysisPhaseSchema>;

export const qvdAnalysisRunSchema = z.object({
  id: z.string().uuid(),
  organizationId: z.string(),
  connectionId: z.string().uuid(),
  // Hash of the run's scope: two runs are comparable only when it matches (and
  // the connection does), so the compare picker can filter the list client-side.
  scopeContextKey: z.string(),
  status: qvdAnalysisRunStatusSchema,
  phase: qvdAnalysisPhaseSchema,
  scope: qvdAnalysisScopeSchema,
  forceFullRefresh: z.boolean(),
  // What the run evaluated (drives which KPIs/tabs the detail view shows).
  // Default true so runs created before these columns render as "all evaluated".
  evaluateTotalSpace: z.boolean().default(true),
  evaluateOrphans: z.boolean().default(true),
  evaluateUnusedColumns: z.boolean().default(true),
  computeColumnSizes: z.boolean(),
  progress: qvdAnalysisProgressSchema.nullable(),
  coverage: qvdAnalysisCoverageSchema.nullable(),
  error: z.string().nullable(),
  startedAt: z.string().nullable(),
  finishedAt: z.string().nullable(),
  createdAt: z.string(),
});
export type QvdAnalysisRun = z.infer<typeof qvdAnalysisRunSchema>;

export const qvdAnalysisRunResponseSchema = z.object({ data: qvdAnalysisRunSchema });
export type QvdAnalysisRunResponse = z.infer<typeof qvdAnalysisRunResponseSchema>;
export const startQvdAnalysisResponseSchema = z.object({
  data: qvdAnalysisRunSchema,
  created: z.boolean(),
});
export type StartQvdAnalysisResponse = z.infer<typeof startQvdAnalysisResponseSchema>;

// --- Result row DTOs ---
export const orphanQvdSchema = z.object({
  id: z.string().uuid(),
  qvdIdentity: z.string(),
  qvdPath: z.string(),
  qvdName: z.string(),
  spaceName: z.string().nullable(),
  spaceId: z.string().nullable().default(null),
  sizeBytes: z.number().nullable(),
  lastModifiedAt: z.string().nullable(),
  writtenByApps: z.array(z.string()).default([]),
  // True = apps DO read other files of this file's name family, but none was
  // observed reading THIS one, so the orphan verdict is "no measured read"
  // rather than "provably unused" and the row is badged as such. Defaults to
  // false for rows persisted before the flag existed.
  consumedByFamilyOnly: z.boolean().default(false),
});
export type OrphanQvd = z.infer<typeof orphanQvdSchema>;

export const columnResultSchema = z.object({
  column: z.string(),
  loaded: z.boolean(),
  // exact disk bytes this column occupies in the QVD (symbol table + row index),
  // present only when the size phase ran. null/absent = not sized.
  sizeBytes: z.number().nullable().optional(),
});
export type ColumnResult = z.infer<typeof columnResultSchema>;

// "size_unavailable" = the format cannot expose per-column bytes at all (Parquet:
// Qlik Cloud serves no file content, so the footer's column chunks are
// unreachable). Distinct from "size_failed" (a QVD whose header read failed and
// could succeed on a retry) and from "unsized" (still queued) — an unavailable
// row is terminal and must never keep a run pending.
export const qvdSizeStatusSchema = z.enum(["unsized", "sized", "size_failed", "size_unavailable"]);
export type QvdSizeStatus = z.infer<typeof qvdSizeStatusSchema>;

// Provenance of a column verdict — shared by the result rows and by the column
// cache that re-emits them, so both sides can never drift apart.
export const qvdAnalysisSourceSchema = z.enum(["direct", "inferred", "lineage", "family"]);
export type QvdAnalysisSource = z.infer<typeof qvdAnalysisSourceSchema>;

// One app that consumes a QVD, plus the fields it loads from that file.
// `fields` empty = the app reads the file but left no per-field evidence
// (LOAD * without a field list). `viaPattern` = the app reads by wildcard —
// legitimate, measured consumption whose fields belong to the matched set
// rather than to this one file. `viaFamily` = the fields were inherited from
// another file of the same name family; it describes the FIELDS, not the read,
// which may be perfectly real. The UI shows one "inherited" badge for either.
// Detail-only payload: it never rides along the (paginated) list rows.
// `spaceName` defaults to "" so rows persisted before it existed (the jsonb
// blob has no such key) keep parsing.
export const consumingAppSchema = z.object({
  appId: z.string(),
  appName: z.string(),
  spaceName: z.string().default(""),
  // Fallback label when spaceName is empty (see unverifiedAppSchema).
  spaceId: z.string().default(""),
  // The app's last reload (RFC3339), shown next to a "no_successful_reload"
  // logStatus. Defaults to "" for rows persisted before it existed.
  lastReloadTime: z.string().default(""),
  fields: z
    .array(z.string())
    .nullish()
    .transform((v) => v ?? []),
  viaPattern: z.boolean().default(false),
  viaFamily: z.boolean().default(false),
  // A third, orthogonal axis: `viaPattern`/`viaFamily` say HOW the file was
  // reached and where the FIELDS came from, `viaWholeFile` says HOW MUCH of the
  // file is read — a `LOAD *` reads every column, so the QVD promises no column
  // savings. Defaults to false for rows persisted before it existed (the jsonb
  // blob has no such key), exactly like `spaceName` above.
  viaWholeFile: z.boolean().default(false),
  // Why this app has no fields, when it has none: "" = its reload log WAS read
  // (so the script simply echoed no field names), "forbidden" |
  // "no_successful_reload" | "unreadable" = the log could not be read at all,
  // making its fields unknown rather than absent. Defaults to "" for rows
  // persisted before it existed, exactly like `spaceName` above.
  logStatus: z.string().default(""),
});
export type ConsumingApp = z.infer<typeof consumingAppSchema>;

export const unloadedColumnsQvdSchema = z.object({
  id: z.string().uuid(),
  qvdIdentity: z.string(),
  qvdPath: z.string(),
  qvdName: z.string(),
  spaceName: z.string().nullable(),
  spaceId: z.string().nullable().default(null),
  status: z.enum(["analyzed", "not_analyzable"]),
  notAnalyzableReason: z.string().nullable(),
  totalColumns: z.number(),
  notLoadedCount: z.number(),
  columnResults: z.array(columnResultSchema),
  notLoadedColumns: z.array(z.string()),
  // Provenance: "direct" (the QVD's own evidence — reload log or its own
  // lineage) | "inferred" (legacy: extrapolated from a name-family sample, kept
  // only for compatibility with rows persisted before family sampling was
  // removed) | "lineage" (verdict derived from Qlik's field impact graph, used
  // when the reload log did not provide the loaded fields) | "family" (every
  // field on the row was inherited from siblings of the same name family, none
  // measured on this file; the consumption itself may be real — unlike
  // "inferred" the per-app detail exists, and the drawer stays open so the user
  // can judge the extrapolation).
  // Existing rows default to "direct" (all were direct before).
  analysisSource: qvdAnalysisSourceSchema.default("direct"),
  // QVD-column-size phase (opt-in). sizeStatus defaults to 'unsized' for runs
  // that didn't compute sizes (and for rows persisted before the feature).
  sizeStatus: qvdSizeStatusSchema.default("unsized"),
  unusedBytes: z.number().nullable().default(null),
  totalBytes: z.number().nullable().default(null),
  // True = the column verdict was WITHHELD: a consuming app has no field
  // evidence at all (its reload log could not be read) AND Qlik's field lineage
  // could not be asked what that app loads either, so every column is
  // reported loaded and no saving is claimed. Zero not-loaded columns here means
  // "unknown", not "all in use" — which is why such a row is kept out of the
  // unloaded-columns list and shown in its own section instead. Defaults to
  // false for rows persisted before the flag existed.
  evidenceIncomplete: z.boolean().default(false),
});
export type UnloadedColumnsQvd = z.infer<typeof unloadedColumnsQvdSchema>;

// --- Paginated list responses ---
export const listOrphanQvdsResponseSchema = z.object({
  data: z.array(orphanQvdSchema),
  nextCursor: z.string().optional(), // opaque (encodes sizeBytes + id)
});
export type ListOrphanQvdsResponse = z.infer<typeof listOrphanQvdsResponseSchema>;

export const listUnloadedColumnsResponseSchema = z.object({
  data: z.array(unloadedColumnsQvdSchema),
  nextCursor: z.string().uuid().optional(),
});
export type ListUnloadedColumnsResponse = z.infer<typeof listUnloadedColumnsResponseSchema>;

// Per-QVD detail (one result row): which apps consume the file and which fields
// each of them loads. Null when the row carries no consumer evidence.
export const qvdConsumingAppsResponseSchema = z.object({
  consumedByApps: z.array(consumingAppSchema).nullable(),
});
export type QvdConsumingAppsResponse = z.infer<typeof qvdConsumingAppsResponseSchema>;

// --- Files (full data-file inventory for a run; every file type, not only QVDs).
// Replaces the old bounded "largest files" top-N: the whole inventory is
// persisted and served with server-side search/space-filter/sort + keyset
// pagination, since a run can hold hundreds of thousands of files. ---
export const qvdFileSchema = z.object({
  id: z.string().uuid(),
  path: z.string(),
  name: z.string(),
  ext: z.string(),
  spaceName: z.string().nullable(),
  sizeBytes: z.number(),
  // How many apps consume this file — the LENGTH of the detail payload, never
  // the payload itself (apps x fields would multiply every page of a list that
  // can hold hundreds of thousands of rows; fetch the list with
  // GET /:id/files/:fileId). Three distinct states, and the null one is the
  // point: null = no consumer evidence at all (a non-QVD file, a run that
  // stopped before the consumption scan, or a run predating the column), 0 =
  // scanned and nobody reads it, >0 = clickable detail. Defaults to null for
  // rows persisted before it existed.
  consumerCount: z.number().nullable().default(null),
});
export type QvdFile = z.infer<typeof qvdFileSchema>;

// Server-side sort keys for the files list. Space + type stay client-side
// filters (a dropdown), not sorts, so the table keeps a lean index set.
export const qvdFilesSortSchema = z.enum(["size", "name"]);
export type QvdFilesSort = z.infer<typeof qvdFilesSortSchema>;

export const listQvdFilesResponseSchema = z.object({
  data: z.array(qvdFileSchema),
  nextCursor: z.string().optional(), // opaque (encodes the active sort key + id)
});
export type ListQvdFilesResponse = z.infer<typeof listQvdFilesResponseSchema>;

// --- Directory drill-down inside ONE space (a level of the inventory's tree).
// Aggregated server-side, one level per request: a space can hold hundreds of
// thousands of files and the list endpoint pages at 200, so the browser never
// gets to build the tree itself. A "dir" child carries its WHOLE subtree's
// bytes/files; a "file" child is a single file. ---
export const qvdDirectoryChildSchema = z.object({
  name: z.string(), // the immediate path segment, not the full path
  kind: z.enum(["dir", "file"]),
  bytes: z.number(),
  fileCount: z.number(),
});
export type QvdDirectoryChild = z.infer<typeof qvdDirectoryChildSchema>;

export const qvdDirectoryResponseSchema = z.object({
  children: z.array(qvdDirectoryChildSchema),
  // Totals of THIS level (the prefix's whole subtree), which is what the per-row
  // % share is measured against — not the space's or the run's total.
  totalBytes: z.number(),
  totalFiles: z.number(),
  // The level held more children than one query returns; the listed ones are the
  // largest. Disclosed as a flag on the level, never as an error — there is
  // nothing to retry and nothing more to page through.
  truncated: z.boolean().default(false),
});
export type QvdDirectoryResponse = z.infer<typeof qvdDirectoryResponseSchema>;

export const listQvdAnalysisRunsResponseSchema = z.object({
  data: z.array(qvdAnalysisRunSchema),
  nextCursor: z.string().uuid().optional(),
});
export type ListQvdAnalysisRunsResponse = z.infer<typeof listQvdAnalysisRunsResponseSchema>;

// --- Run comparison (on-demand set diff by qri between two completed runs of
// the same connection + scope; nothing is persisted). Only added/removed files
// are exposed — a file present in both runs is not a change worth listing. The
// per-side listing reuses listQvdFilesResponseSchema (same rows, same cursor). ---
export const qvdRunDiffSideSchema = z.enum(["added", "removed"]);
export type QvdRunDiffSide = z.infer<typeof qvdRunDiffSideSchema>;

export const qvdRunDiffSummarySchema = z.object({
  // from = the OLDER run of the pair, whichever side the caller sent as :id.
  fromRunId: z.string().uuid(),
  toRunId: z.string().uuid(),
  fromFinishedAt: z.string().nullable(),
  toFinishedAt: z.string().nullable(),
  fromFileCount: z.number(),
  fromTotalBytes: z.number(),
  toFileCount: z.number(),
  toTotalBytes: z.number(),
  addedCount: z.number(),
  addedBytes: z.number(),
  removedCount: z.number(),
  removedBytes: z.number(),
});
export type QvdRunDiffSummary = z.infer<typeof qvdRunDiffSummarySchema>;

export const qvdRunCompareResponseSchema = z.object({ data: qvdRunDiffSummarySchema });
export type QvdRunCompareResponse = z.infer<typeof qvdRunCompareResponseSchema>;

// --- Run logs (the worker's own execution log stream for a run, teed to the
// API in batches so the UI can show what happened without server access). ---
export const qvdRunLogLevelSchema = z.enum(["info", "warn", "error"]);
export type QvdRunLogLevel = z.infer<typeof qvdRunLogLevelSchema>;

export const qvdRunLogSchema = z.object({
  // bigserial insert-order id, serialized as a string (the list's sort key +
  // pagination cursor)
  id: z.string(),
  ts: z.string(), // RFC3339 (the record's timestamp on the worker)
  level: qvdRunLogLevelSchema,
  message: z.string(),
  // structured slog attrs (key → value); null when the record had none
  attrs: z.record(z.string(), z.unknown()).nullable(),
});
export type QvdRunLog = z.infer<typeof qvdRunLogSchema>;

export const listQvdRunLogsResponseSchema = z.object({
  data: z.array(qvdRunLogSchema),
  nextCursor: z.string().optional(), // the last row's id
});
export type ListQvdRunLogsResponse = z.infer<typeof listQvdRunLogsResponseSchema>;

// --- Worker -> API callback payloads (WORKER_SECRET routes) ---
export const saveQvdInventoryProgressSchema = z.object({
  runId: z.string().uuid(),
  organizationId: z.string(),
  phase: qvdAnalysisPhaseSchema,
  progress: qvdAnalysisProgressSchema,
  coverage: qvdAnalysisCoverageSchema.partial().optional(),
});
export type SaveQvdInventoryProgress = z.infer<typeof saveQvdInventoryProgressSchema>;

export const saveQvdOrphansSchema = z.object({
  runId: z.string().uuid(),
  organizationId: z.string(),
  orphans: z.array(orphanQvdSchema.omit({ id: true })),
});
export type SaveQvdOrphans = z.infer<typeof saveQvdOrphansSchema>;

export const saveQvdUnloadedColumnsSchema = z.object({
  runId: z.string().uuid(),
  organizationId: z.string(),
  // The consuming-apps matrix rides only on the SAVE payload (and is served by
  // the per-row detail route) — never on the list response, which the table
  // fetches page by page.
  rows: z.array(
    unloadedColumnsQvdSchema
      .omit({ id: true })
      .extend({ consumedByApps: z.array(consumingAppSchema).optional() }),
  ),
});
export type SaveQvdUnloadedColumns = z.infer<typeof saveQvdUnloadedColumnsSchema>;

// Worker streams the full file inventory in batches (idempotent upsert on the
// API side, keyed by the stable per-file qri).
export const saveQvdFilesSchema = z.object({
  runId: z.string().uuid(),
  organizationId: z.string(),
  files: z.array(
    z.object({
      qri: z.string(),
      path: z.string(),
      name: z.string(),
      ext: z.string(),
      spaceName: z.string().nullable(),
      sizeBytes: z.number(),
    }),
  ),
});
export type SaveQvdFiles = z.infer<typeof saveQvdFilesSchema>;

// Second pass over the file inventory: the consuming apps of each QVD, attached
// to the FILE row. It is a separate payload because the file rows are written
// during the inventory phase, BEFORE any consumer is known — this one only
// enriches existing (runId, qri) rows and never creates or rewrites them.
// A QVD nobody reads is sent with an EMPTY list, which is what lets the file row
// distinguish "scanned, no consumer" from the NULL "never scanned".
export const saveQvdFileConsumersSchema = z.object({
  runId: z.string().uuid(),
  organizationId: z.string(),
  files: z.array(
    z.object({
      qri: z.string(),
      consumedByApps: z.array(consumingAppSchema),
    }),
  ),
});
export type SaveQvdFileConsumers = z.infer<typeof saveQvdFileConsumersSchema>;

// Worker streams the run's log entries in batches (append-only; ordering comes
// from the API-side bigserial id — a single writer per run flushes in order).
export const saveQvdRunLogsSchema = z.object({
  runId: z.string().uuid(),
  organizationId: z.string(),
  entries: z
    .array(
      z.object({
        ts: z.string(),
        level: qvdRunLogLevelSchema,
        message: z.string().max(4000),
        attrs: z.record(z.string(), z.unknown()).optional(),
      }),
    )
    .max(500),
});
export type SaveQvdRunLogs = z.infer<typeof saveQvdRunLogsSchema>;

export const saveQvdColumnCacheSchema = z.object({
  organizationId: z.string(),
  connectionId: z.string().uuid(),
  entries: z.array(
    z.object({
      qvdIdentity: z.string(),
      contentUpdatedDate: z.string().nullable(),
      consumerFingerprint: z.string(),
      columnResults: z.array(columnResultSchema),
      // Provenance of the cached verdict, so a cache hit re-emits the row with
      // the source the original analysis earned instead of silently downgrading
      // it. Defaults to "direct" so a worker that does not send the field yet
      // keeps writing valid entries.
      analysisSource: qvdAnalysisSourceSchema.default("direct"),
    }),
  ),
});
export type SaveQvdColumnCache = z.infer<typeof saveQvdColumnCacheSchema>;

export const qvdLibTargetSchema = z.object({
  space: z.string(),
  path: z.string(),
  // `wholeFile` = the read's field list was `*`, so every column of the file is
  // loaded. It rides on the target, inside the existing `reads`/`readPatterns`
  // jsonb, so no migration is needed and — crucially — the fact survives the
  // app-consumption cache: a cached app (lastReloadTime unchanged, i.e. most
  // apps on a re-run) never re-parses its log, and a flag stored anywhere else
  // would come back empty. `.default(false)` reads cache entries written before
  // this existed as "not whole-file", which is the honest answer for them: they
  // were never measured.
  wholeFile: z.boolean().default(false),
});
export type QvdLibTarget = z.infer<typeof qvdLibTargetSchema>;

export const qvdAppConsumptionEntrySchema = z.object({
  appId: z.string().min(1),
  appName: z.string().default(""),
  spaceId: z.string().nullable().default(null),
  lastReloadTime: z.string().min(1),
  reloadId: z.string().default(""),
  status: z.string().min(1), // "parsed" | "unreadable:<reason>"
  reads: z.array(qvdLibTargetSchema).default([]),
  readPatterns: z.array(qvdLibTargetSchema).default([]),
  writes: z.array(qvdLibTargetSchema).default([]),
  fieldsByFile: z.record(z.string(), z.array(z.string())).default({}),
});
export type QvdAppConsumptionEntry = z.infer<typeof qvdAppConsumptionEntrySchema>;

export const saveQvdAppConsumptionSchema = z.object({
  organizationId: z.string().min(1),
  connectionId: z.string().min(1),
  entries: z.array(qvdAppConsumptionEntrySchema).max(500),
});
export type SaveQvdAppConsumption = z.infer<typeof saveQvdAppConsumptionSchema>;

export const finalizeQvdRunSchema = z.object({
  runId: z.string().uuid(),
  organizationId: z.string(),
  status: z.enum(["completed", "failed", "cancelled"]),
  coverage: qvdAnalysisCoverageSchema,
  error: z.string().optional(),
});
export type FinalizeQvdRun = z.infer<typeof finalizeQvdRunSchema>;

// --- Query strings (the API validates them; the CLI validates flags). ---
export const qvdAnalysisListQuerySchema = z
  .object({ connectionId: z.string().uuid().optional() })
  .passthrough();

export const qvdScopeOptionsQuerySchema = z
  .object({ connectionId: z.string().uuid() })
  .passthrough();

export const qvdExportQuerySchema = z.object({
  type: z.enum(["orphans", "columns"]),
  format: z.enum(["csv", "json"]),
});

export const qvdFilesQuerySchema = z
  .object({
    sort: qvdFilesSortSchema.default("size"),
    search: z.string().trim().min(1).max(200).optional(),
    space: z.string().trim().min(1).max(200).optional(),
  })
  .passthrough();

// Directory drill-down inside one space. `prefix` is a path fragment, not a
// cursor — the service rejects one that does not name a directory.
export const qvdDirectoriesQuerySchema = z
  .object({
    space: z.string().trim().min(1).max(200),
    prefix: z.string().max(1000).default(""),
  })
  .passthrough();

// Same list as /files, restricted to one side of the run diff.
export const qvdCompareFilesQuerySchema = qvdFilesQuerySchema.extend({
  side: qvdRunDiffSideSchema,
});

// The columns list serves two DISJOINT populations of the same table with the
// same row shape: the QVDs with a column verdict (default) and the ones whose
// verdict was withheld because a consuming app has no field evidence
// (evidence=incomplete). One route, one filter — a second endpoint would only
// duplicate the pagination.
export const qvdColumnsQuerySchema = z
  .object({ evidence: z.enum(["complete", "incomplete"]).default("complete") })
  .passthrough();

// level = MINIMUM severity shown ("info" → everything, "warn" → warn+error).
export const qvdLogsQuerySchema = z
  .object({ level: qvdRunLogLevelSchema.optional() })
  .passthrough();
